import React, { useMemo, useEffect, useRef, useState } from "react";
import { StyleSheet, Text, View, Animated } from "react-native";
import { observer } from "mobx-react";
import { AntDesign, Ionicons } from "@expo/vector-icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import Tooltip from "../../../Components/Tooltip";
import { getLocalizedFoodName } from "../../foods/foodCatalog";
import { FoodPickerPanel } from "../../shared/foodPicker/FoodPickerPanel";
import { getEditorFoodSourceLabel } from "../../shared/editorFoodSourceLabel";
import { SelectedFoodRow } from "../../shared/foodRows/SelectedFoodRow";
import { getSelectedRowDeleteTooltipKey } from "../../shared/foodRows/selectedRowDeleteTooltip";
import {
  formatQuantityInGramsText,
  getFoodStableKey,
  getInitialFoodQuantityInGrams,
  getIngredientQuantityInGrams,
  getIngredientSelectedUnitKey,
  getIngredientSourceLabel,
  getNutritionTotalsForQuantity,
  getQuantityUnitOptions,
  isEquivalentQuantityManual,
  RECIPE_TYPE_OPTIONS,
} from "../recipeLogic";

const EQUIVALENCE_TREE_VISUAL_GAP = 12;
const EQUIVALENCE_TREE_CARD_VERTICAL_PADDING = EQUIVALENCE_TREE_VISUAL_GAP / 2;

function SelectedEquivalenceCard({
  parentIngredientId,
  equivalence,
  isLast,
  useStackedIngredientLayout,
  getIngredientTotals,
  updateSelectedEquivalenceQuantity,
  changeSelectedEquivalenceUnit,
  updateSelectedEquivalenceName,
  toggleSelectedEquivalenceQuantityMode,
  removeEquivalence,
  t,
}) {
  const equivalenceTotals = getIngredientTotals(equivalence);
  const isLinked = !isEquivalentQuantityManual(equivalence);
  const linkTooltip = isLinked
    ? t(
        "nutritionSwitchToManualQuantityTooltip-string",
        "Switch to manual quantity",
      )
    : t(
        "nutritionLinkEquivalentQuantityTooltip-string",
        "Link to main food",
      );

  return (
    <SelectedFoodRow
      title={equivalence.name}
      subtitle={getEditorFoodSourceLabel(equivalence, t)}
      quantityValue={String(equivalence.quantity ?? "")}
      onChangeQuantity={(value) =>
        updateSelectedEquivalenceQuantity(parentIngredientId, equivalence.id, value)
      }
      unitOptions={getQuantityUnitOptions(equivalence, { t })}
      selectedUnitKey={getIngredientSelectedUnitKey(equivalence)}
      onSelectUnit={(nextUnitKey) =>
        changeSelectedEquivalenceUnit(parentIngredientId, equivalence.id, nextUnitKey)
      }
      isTitleEditable={true}
      onRenameTitle={(nextName) =>
        updateSelectedEquivalenceName(parentIngredientId, equivalence.id, nextName)
      }
      referenceText={formatQuantityInGramsText(
        getIngredientQuantityInGrams(equivalence),
      )}
      totals={equivalenceTotals}
      compactLayout={useStackedIngredientLayout}
      isLast={isLast}
      chromeVariant="transparent"
      showDivider={false}
      showNutritionSummaryCalories={false}
      isAutoCalculated={isLinked}
      autoCalculatedTooltip={t(
        "nutritionAutoCalculatedEquivalentTooltip-string",
        "Auto-calculated to match energy",
      )}
      quantityLinkTooltip={linkTooltip}
      onToggleQuantityLink={() =>
        toggleSelectedEquivalenceQuantityMode(
          parentIngredientId,
          equivalence.id,
        )
      }
      containerStyle={styles.selectedEquivalenceCard}
      leadingSlot={
        <View style={styles.equivalenceIconWrap}>
          <Ionicons
            name="git-compare-outline"
            size={14}
            color={Colors.bubbleColor}
          />
        </View>
      }
      actionSlot={
        <View style={[styles.rowActionGroup, styles.rowActionGroupCompact]}>
          <Tooltip
            content={getSelectedRowDeleteTooltipKey({
              entityKind: "equivalent",
            })}
            placement="top"
          >
            <View>
              <HoverableTouchableOpacity
                activeOpacity={0.85}
                onPress={() => removeEquivalence(parentIngredientId, equivalence.id)}
                style={[styles.removeIngredientButton, styles.equivalenceRemoveButton]}
                hoverStyle={styles.removeIngredientButtonHover}
              >
                <Ionicons
                  name="trash-outline"
                  size={16}
                  color={Colors.darkgrayColor}
                />
              </HoverableTouchableOpacity>
            </View>
          </Tooltip>
        </View>
      }
    />
  );
}

const SortableIngredientCard = observer(function SortableIngredientCard({
  ingredient,
  isLast,
  useStackedIngredientLayout,
  getIngredientTotals,
  equivalenceTargetId,
  setEquivalenceTargetId,
  updateSelectedIngredientQuantity,
  changeSelectedIngredientUnit,
  updateSelectedIngredientName,
  updateSelectedEquivalenceQuantity,
  changeSelectedEquivalenceUnit,
  updateSelectedEquivalenceName,
  toggleSelectedEquivalenceQuantityMode,
  removeIngredient,
  removeEquivalence,
  t,
  equivalencePickerPanelProps,
  equivalencePickerPanelRootRef,
  equivalenceTargetIngredient,
  closeIngredientBrowser,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ingredient.id });

  const style = {
    transform: CSS.Transform.toString(
      transform
        ? {
            ...transform,
            scaleX: 1,
            scaleY: 1,
          }
        : null,
    ),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 0,
    position: "relative",
  };

  const ingredientTotals = getIngredientTotals(ingredient);
  const isEquivalenceTarget = equivalenceTargetId === ingredient.id;
  const equivalences = Array.isArray(ingredient.equivalences)
    ? ingredient.equivalences
    : [];

  return (
    <View ref={setNodeRef} style={style}>
      <SelectedFoodRow
        title={ingredient.name}
        subtitle={getEditorFoodSourceLabel(ingredient, t)}
        quantityValue={String(ingredient.quantity ?? "")}
        onChangeQuantity={(value) =>
          updateSelectedIngredientQuantity(ingredient.id, value)
        }
        unitOptions={getQuantityUnitOptions(ingredient, { t })}
        selectedUnitKey={getIngredientSelectedUnitKey(ingredient)}
        onSelectUnit={(nextUnitKey) =>
          changeSelectedIngredientUnit(ingredient.id, nextUnitKey)
        }
        isTitleEditable={true}
        onRenameTitle={(nextName) =>
          updateSelectedIngredientName(ingredient.id, nextName)
        }
        referenceText={formatQuantityInGramsText(
          getIngredientQuantityInGrams(ingredient),
        )}
        totals={ingredientTotals}
        compactLayout={useStackedIngredientLayout}
        isLast={isLast}
        chromeVariant="transparent"
        dragHandleProps={{
          attributes,
          listeners,
          isDragging,
        }}
        actionSlot={
          <View
            style={[
              styles.rowActionGroup,
              useStackedIngredientLayout ? styles.rowActionGroupCompact : null,
            ]}
          >
            <Tooltip content="addAnotherOption-string" placement="top">
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={() =>
                    setEquivalenceTargetId((currentTargetId) =>
                      currentTargetId === ingredient.id ? null : ingredient.id,
                    )
                  }
                  style={[
                    styles.rowActionButton,
                    isEquivalenceTarget ? styles.rowActionButtonActive : null,
                  ]}
                  hoverStyle={styles.rowActionButtonHover}
                >
                  <Ionicons
                    name="git-compare-outline"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
            <Tooltip
              content={getSelectedRowDeleteTooltipKey({
                entityKind: "food",
                equivalenceCount: equivalences.length,
              })}
              placement="top"
            >
              <View>
                <HoverableTouchableOpacity
                  activeOpacity={0.85}
                  onPress={() => removeIngredient(ingredient.id)}
                  style={styles.removeIngredientButton}
                  hoverStyle={styles.removeIngredientButtonHover}
                >
                  <Ionicons
                    name="trash-outline"
                    size={16}
                    color={Colors.darkgrayColor}
                  />
                </HoverableTouchableOpacity>
              </View>
            </Tooltip>
          </View>
        }
        footer={
          <>
            {equivalences.length > 0 ? (
              <View style={styles.selectedIngredientEquivalencesRow}>
                {equivalences.map((equivalence, equivalenceIndex) => {
                  const isFirstEquivalence = equivalenceIndex === 0;
                  const isLastEquivalence =
                    equivalenceIndex === equivalences.length - 1;

                  return (
                    <View
                      key={equivalence.id}
                      style={[
                        styles.equivalenceTreeRowContainer,
                        isLastEquivalence
                          ? styles.equivalenceTreeRowContainerLast
                          : null,
                      ]}
                    >
                      <View style={styles.equivalenceTreeLineContainer}>
                        <View
                          style={[
                            styles.equivalenceTreeVerticalLine,
                            isFirstEquivalence
                              ? styles.equivalenceTreeVerticalLineFirst
                              : null,
                            isLastEquivalence
                              ? styles.equivalenceTreeVerticalLineLast
                              : null,
                          ]}
                        />
                        <View style={styles.equivalenceTreeHorizontalLine} />
                        <View style={styles.equivalenceTreeDot} />
                      </View>
                      <View style={styles.equivalenceTreeCardWrap}>
                        <SelectedEquivalenceCard
                          parentIngredientId={ingredient.id}
                          equivalence={equivalence}
                          isLast={isLastEquivalence}
                          useStackedIngredientLayout={useStackedIngredientLayout}
                          getIngredientTotals={getIngredientTotals}
                          updateSelectedEquivalenceQuantity={
                            updateSelectedEquivalenceQuantity
                          }
                          changeSelectedEquivalenceUnit={
                            changeSelectedEquivalenceUnit
                          }
                          updateSelectedEquivalenceName={
                            updateSelectedEquivalenceName
                          }
                          toggleSelectedEquivalenceQuantityMode={
                            toggleSelectedEquivalenceQuantityMode
                          }
                          removeEquivalence={removeEquivalence}
                          t={t}
                        />
                      </View>
                    </View>
                  );
                })}
              </View>
            ) : null}
            {isEquivalenceTarget ? (
              <FoodPickerPanel
                {...equivalencePickerPanelProps}
                panelRootRef={equivalencePickerPanelRootRef}
                title={t("addEquivalent-string")}
                subtitle={t("addingEquivalentTo-string", {
                  name: equivalenceTargetIngredient?.name,
                })}
                onClose={closeIngredientBrowser}
                isInline={true}
              />
            ) : null}
          </>
        }
        trimBottomPadding={isEquivalenceTarget}
      />
    </View>
  );
});

export const RecipeIngredientsSection = observer(function RecipeIngredientsSection({
  t,
  form,
  saveValidationMessage,
  sensors,
  handleDragEnd,
  handleDragStart,
  useStackedIngredientLayout,
  getIngredientTotals,
  equivalenceTargetId,
  setEquivalenceTargetId,
  updateSelectedIngredientQuantity,
  changeSelectedIngredientUnit,
  updateSelectedIngredientName,
  updateSelectedEquivalenceQuantity,
  changeSelectedEquivalenceUnit,
  updateSelectedEquivalenceName,
  toggleSelectedEquivalenceQuantityMode,
  removeIngredient,
  removeEquivalence,
  searchInputRef,
  ingredientSearchInputPressingRef,
  focusIngredientSearchInput,
  ingredientSearchText,
  setIngredientSearchText,
  ingredientSortOptions,
  selectedSortOption,
  ingredientSortKey,
  setIngredientSortKey,
  mainFilterOptions,
  selectedMainFilterOption,
  mainFilterKey,
  setMainFilterKey,
  mainIngredientResults,
  mainIngredientIsLoading,
  mainIngredientLoadingSessionId,
  inlineFilterOptions,
  selectedInlineFilterOption,
  inlineFilterKey,
  setInlineFilterKey,
  inlineIngredientResults,
  inlineIngredientIsLoading,
  inlineIngredientLoadingSessionId,
  language,
  addPickerIngredient,
  addInlineEquivalentIngredient,
  onCreateFood,
  equivalenceTargetIngredient,
  closeIngredientBrowser,
  isIngredientBrowserOpen,
  openMainIngredientBrowser,
  mainPickerPanelRootRef,
  equivalencePickerPanelRootRef,
}) {
  const mainPickerPanelProps = useMemo(
    () => ({
      t,
      searchInputRef,
      inputPressingRef: ingredientSearchInputPressingRef,
      focusSearchInput: focusIngredientSearchInput,
      searchText: ingredientSearchText,
      setSearchText: setIngredientSearchText,
      sortOptions: ingredientSortOptions,
      selectedSortOption,
      sortKey: ingredientSortKey,
      setSortKey: setIngredientSortKey,
      databaseOptions: mainFilterOptions,
      selectedDatabaseOption: selectedMainFilterOption,
      databaseKey: mainFilterKey,
      setDatabaseKey: setMainFilterKey,
      results: mainIngredientResults,
      isLoading: mainIngredientIsLoading,
      loadingSessionId: mainIngredientLoadingSessionId,
      showRowQuantityControl: false,
      getRowKey: (item, index) =>
        String(item?.key || item?.id || `${getFoodStableKey(item, "en")}-${index}`),
      getRowTitle: (item) => item?.title ?? "",
      getRowSubtitle: (item) => item?.subtitle ?? "",
      getRowEntityKind: (item) => item?.entityKind ?? "food",
      getRowEntityLabel: (item) =>
        item?.entityKind === "equivalent"
          ? t("equivalentResultType-string", "Equivalent")
          : item?.entityKind === "recipe"
            ? t("recipeResultType-string", "Recipe")
            : null,
      getRowPreviewText: (item) => item?.previewText ?? "",
      getRowReferenceText: () => "",
      getRowTotals: (item) => item?.nutrients ?? null,
      canSelectRow: () => true,
      onSelectRow: addPickerIngredient,
      onCreateFood,
      emptyText: t("noPickerResults-string", "No results match this search"),
      compactLayout: useStackedIngredientLayout,
      searchPlaceholder: t(
        "searchFoodOrEquivalent-string",
        "Search food or equivalent",
      ),
    }),
    [
      addPickerIngredient,
      ingredientSearchInputPressingRef,
      ingredientSearchText,
      ingredientSortKey,
      focusIngredientSearchInput,
      ingredientSortOptions,
      mainFilterOptions,
      mainFilterKey,
      mainIngredientIsLoading,
      mainIngredientLoadingSessionId,
      mainIngredientResults,
      onCreateFood,
      searchInputRef,
      selectedMainFilterOption,
      selectedSortOption,
      setIngredientSearchText,
      setIngredientSortKey,
      setMainFilterKey,
      t,
      useStackedIngredientLayout,
    ],
  );

  const equivalencePickerPanelProps = useMemo(
    () => ({
      t,
      searchInputRef,
      inputPressingRef: ingredientSearchInputPressingRef,
      focusSearchInput: focusIngredientSearchInput,
      searchText: ingredientSearchText,
      setSearchText: setIngredientSearchText,
      sortOptions: ingredientSortOptions,
      selectedSortOption,
      sortKey: ingredientSortKey,
      setSortKey: setIngredientSortKey,
      databaseOptions: inlineFilterOptions,
      selectedDatabaseOption: selectedInlineFilterOption,
      databaseKey: inlineFilterKey,
      setDatabaseKey: setInlineFilterKey,
      results: inlineIngredientResults,
      isLoading: inlineIngredientIsLoading,
      loadingSessionId: inlineIngredientLoadingSessionId,
      showRowQuantityControl: false,
      getRowKey: (food, index) =>
        String(food?.id || food?.code || `${getFoodStableKey(food, language)}-${index}`),
      getRowTitle: (food) => getLocalizedFoodName(food, language),
      getRowSubtitle: (food) => getIngredientSourceLabel(food, t),
      getRowReferenceText: (food) =>
        formatQuantityInGramsText(getInitialFoodQuantityInGrams(food)),
      getRowTotals: (food) =>
        getNutritionTotalsForQuantity(food, getInitialFoodQuantityInGrams(food)),
      canSelectRow: () => true,
      onSelectRow: addInlineEquivalentIngredient,
      onCreateFood,
      emptyText: t("noIngredientResults-string"),
      compactLayout: useStackedIngredientLayout,
    }),
    [
      addInlineEquivalentIngredient,
      focusIngredientSearchInput,
      ingredientSearchInputPressingRef,
      ingredientSearchText,
      ingredientSortKey,
      ingredientSortOptions,
      inlineFilterKey,
      inlineIngredientIsLoading,
      inlineIngredientLoadingSessionId,
      inlineFilterOptions,
      inlineIngredientResults,
      language,
      onCreateFood,
      selectedInlineFilterOption,
      selectedSortOption,
      setIngredientSearchText,
      setIngredientSortKey,
      setInlineFilterKey,
      t,
      useStackedIngredientLayout,
    ],
  );

  const currentTypeOption = useMemo(
    () => RECIPE_TYPE_OPTIONS.find((opt) => opt.key === form.typeKey) || RECIPE_TYPE_OPTIONS[0],
    [form.typeKey]
  );

  const scaleValue = useRef(new Animated.Value(1)).current;
  const opacityValue = useRef(new Animated.Value(1)).current;
  const prevIconRef = useRef(currentTypeOption.icon);
  const [displayIcon, setDisplayIcon] = useState(currentTypeOption.icon);

  useEffect(() => {
    if (currentTypeOption.icon !== prevIconRef.current) {
      Animated.sequence([
        Animated.parallel([
          Animated.timing(scaleValue, {
            toValue: 0.6,
            duration: 120,
            useNativeDriver: true,
          }),
          Animated.timing(opacityValue, {
            toValue: 0,
            duration: 120,
            useNativeDriver: true,
          }),
        ]),
        Animated.timing(scaleValue, {
          toValue: 1.2,
          duration: 0,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setDisplayIcon(currentTypeOption.icon);
        prevIconRef.current = currentTypeOption.icon;

        Animated.parallel([
          Animated.spring(scaleValue, {
            toValue: 1,
            friction: 6,
            tension: 40,
            useNativeDriver: true,
          }),
          Animated.timing(opacityValue, {
            toValue: 1,
            duration: 150,
            useNativeDriver: true,
          }),
        ]).start();
      });
    }
  }, [currentTypeOption.icon, scaleValue, opacityValue]);

  return (
    <>
      <View style={styles.ingredientsSectionHeader}>
        <View style={styles.ingredientsSectionLine} />
        <Text style={styles.ingredientsSectionLabel}>{t("foods-string")}</Text>
        <View style={styles.ingredientsSectionLine} />
      </View>

      {saveValidationMessage ? (
        <View style={styles.saveNotice}>
          <Ionicons
            name="alert-circle-outline"
            size={14}
            color={Colors.warningTextColor}
          />
          <Text style={styles.saveNoticeText}>{saveValidationMessage}</Text>
        </View>
      ) : null}

      {form.ingredients.length === 0 ? (
        <View style={styles.selectedIngredientsEmpty}>
          <View style={styles.emptyPrepStationBoard}>
            <View style={styles.emptyPrepStationGlow} />

            <View style={[styles.floatingIngredient, styles.floatingIngredientLeft]}>
              <Ionicons name="nutrition-outline" size={16} color={Colors.bubbleColor} />
            </View>

            <View style={styles.prepBoardGraphic}>
              <View style={styles.prepBoardHandleCutout} />
              <Animated.View style={{ transform: [{ scale: scaleValue }], opacity: opacityValue }}>
                <Ionicons name={displayIcon} size={28} color={Colors.bubbleColor} />
              </Animated.View>
              <View style={styles.prepBoardAddBadge}>
                <Ionicons name="add" size={14} color={Colors.whiteColor} />
              </View>
            </View>

            <View style={[styles.floatingIngredient, styles.floatingIngredientRight]}>
              <Ionicons name="leaf-outline" size={16} color={Colors.bubbleColor} />
            </View>
          </View>
          <Text style={styles.selectedIngredientsEmptyTitle}>
            {t("emptyRecipeFunnyTitle-string")}
          </Text>
          <Text style={styles.selectedIngredientsEmptyBody}>
            {t("emptyRecipeFunnySubtitle-string")}
          </Text>
        </View>
      ) : (
        <View style={styles.selectedIngredientsList}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={form.ingredients.map((ingredient) => ingredient.id)}
              strategy={verticalListSortingStrategy}
            >
              {form.ingredients.map((ingredient, index) => (
                <SortableIngredientCard
                  key={ingredient.id}
                  ingredient={ingredient}
                  isLast={index === form.ingredients.length - 1}
                  useStackedIngredientLayout={useStackedIngredientLayout}
                  getIngredientTotals={getIngredientTotals}
                  equivalenceTargetId={equivalenceTargetId}
                  setEquivalenceTargetId={setEquivalenceTargetId}
                  updateSelectedIngredientQuantity={updateSelectedIngredientQuantity}
                  changeSelectedIngredientUnit={changeSelectedIngredientUnit}
                  updateSelectedIngredientName={updateSelectedIngredientName}
                  updateSelectedEquivalenceQuantity={
                    updateSelectedEquivalenceQuantity
                  }
                  changeSelectedEquivalenceUnit={changeSelectedEquivalenceUnit}
                  updateSelectedEquivalenceName={updateSelectedEquivalenceName}
                  toggleSelectedEquivalenceQuantityMode={
                    toggleSelectedEquivalenceQuantityMode
                  }
                  removeIngredient={removeIngredient}
                  removeEquivalence={removeEquivalence}
                  t={t}
                  equivalencePickerPanelProps={equivalencePickerPanelProps}
                  equivalencePickerPanelRootRef={equivalencePickerPanelRootRef}
                  equivalenceTargetIngredient={equivalenceTargetIngredient}
                  closeIngredientBrowser={closeIngredientBrowser}
                />
              ))}
            </SortableContext>
          </DndContext>
        </View>
      )}

      {!equivalenceTargetIngredient && isIngredientBrowserOpen ? (
        <FoodPickerPanel
          {...mainPickerPanelProps}
          panelRootRef={mainPickerPanelRootRef}
          title={t("addFood-string")}
          subtitle={t(
            "recipeAddPickerSubtitle-string",
            "Search foods and equivalents and add them to this recipe.",
          )}
          onClose={closeIngredientBrowser}
          isInline={true}
          topMarginOverride={10}
        />
      ) : null}

      {!equivalenceTargetIngredient && !isIngredientBrowserOpen ? (
        <HoverableTouchableOpacity
          activeOpacity={0.85}
          onPress={openMainIngredientBrowser}
          style={styles.reopenIngredientBrowserButton}
          hoverStyle={styles.reopenIngredientBrowserButtonHover}
        >
          <AntDesign name="plus" size={13} color={Colors.bubbleColor} />
          <Text style={styles.reopenIngredientBrowserButtonText}>
            {t("addNewIngredient-string")}
          </Text>
        </HoverableTouchableOpacity>
      ) : null}
    </>
  );
});

const styles = StyleSheet.create({
  ingredientsSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 18,
    marginBottom: 14,
  },
  ingredientsSectionLabel: {
    ...Fonts.medium12,
    color: Colors.grayColor,
    fontWeight: "400",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    paddingHorizontal: 4,
  },
  ingredientsSectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  saveNotice: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderRadius: 10,
    backgroundColor: Colors.warningBackgroundColor,
    borderWidth: 0.5,
    borderColor: "rgba(194, 65, 12, 0.2)",
    marginBottom: 12,
  },
  saveNoticeText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
    color: Colors.darkgrayColor,
  },
  selectedIngredientsEmpty: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 44,
    gap: 8,
  },
  emptyPrepStationBoard: {
    width: 248,
    maxWidth: "100%",
    minHeight: 120,
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.12)",
    backgroundColor: "rgba(63, 123, 141, 0.035)",
    overflow: "hidden",
    marginBottom: 8,
  },
  emptyPrepStationGlow: {
    position: "absolute",
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: "rgba(63, 123, 141, 0.04)",
  },
  prepBoardGraphic: {
    width: 68,
    height: 84,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.16)",
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1,
  },
  prepBoardHandleCutout: {
    position: "absolute",
    top: 8,
    width: 24,
    height: 6,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.12)",
    backgroundColor: "rgba(63, 123, 141, 0.04)",
  },
  prepBoardAddBadge: {
    position: "absolute",
    bottom: -8,
    right: -8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: Colors.whiteColor,
  },
  floatingIngredient: {
    position: "absolute",
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderWidth: 1,
    borderColor: "rgba(63, 123, 141, 0.15)",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  floatingIngredientLeft: {
    left: 42,
    top: 24,
    transform: [{ rotate: "-15deg" }],
  },
  floatingIngredientRight: {
    right: 42,
    bottom: 24,
    transform: [{ rotate: "15deg" }],
  },
  selectedIngredientsEmptyTitle: {
    ...Fonts.medium14,
    color: Colors.darkgrayColor,
    textAlign: "center",
  },
  selectedIngredientsEmptyBody: {
    maxWidth: 300,
    fontSize: 13,
    lineHeight: 19,
    color: Colors.grayColor,
    textAlign: "center",
  },
  selectedIngredientsList: {
    position: "relative",
    zIndex: 2,
  },
  rowActionGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    width: 80,
    flexShrink: 0,
    justifyContent: "flex-end",
  },
  rowActionGroupCompact: {
    width: "auto",
  },
  rowActionButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    backgroundColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  rowActionButtonHover: {
    backgroundColor: Colors.hoveredGray,
  },
  rowActionButtonActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  removeIngredientButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  removeIngredientButtonHover: {
    backgroundColor: "rgba(240, 68, 56, 0.05)",
    borderColor: "rgba(240, 68, 56, 0.18)",
  },
  selectedIngredientEquivalencesRow: {
    width: "100%",
    marginTop: EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
    gap: 0,
  },
  selectedEquivalenceCard: {
    paddingVertical: EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeRowContainer: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  equivalenceTreeRowContainerLast: {
    marginBottom: -EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeLineContainer: {
    width: 34,
    position: "relative",
    flexShrink: 0,
  },
  equivalenceTreeVerticalLine: {
    position: "absolute",
    left: 11.5,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: Colors.borderColor,
  },
  equivalenceTreeVerticalLineFirst: {
    top: -EQUIVALENCE_TREE_CARD_VERTICAL_PADDING,
  },
  equivalenceTreeVerticalLineLast: {
    bottom: "50%",
  },
  equivalenceTreeHorizontalLine: {
    position: "absolute",
    left: 11.5,
    top: "50%",
    marginTop: -0.5,
    width: 14,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  equivalenceTreeDot: {
    position: "absolute",
    left: 24.5,
    top: "50%",
    marginTop: -3,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.whiteColor,
    borderWidth: 1.5,
    borderColor: Colors.bubbleColor,
  },
  equivalenceTreeCardWrap: {
    flex: 1,
    minWidth: 0,
  },
  equivalenceIconWrap: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexShrink: 0,
  },
  equivalenceRemoveButton: {
    borderWidth: 0,
  },
  reopenIngredientBrowserButton: {
    marginTop: 10,
    minHeight: 42,
    borderRadius: 12,
    backgroundColor: "rgba(63, 123, 141, 0.08)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transitionProperty: "opacity",
    transitionDuration: "160ms",
    transitionTimingFunction: "ease",
  },
  reopenIngredientBrowserButtonHover: {
    opacity: 0.82,
  },
  reopenIngredientBrowserButtonText: {
    ...Fonts.medium13,
    color: Colors.bubbleColor,
  },
});
